import { FC, ReactNode, useCallback, useMemo } from 'react';
import ReactSelect, {
  GroupBase,
  OnChangeValue,
  Props,
  SelectComponentsConfig,
  SingleValue,
} from 'react-select';

import { Control, Option, Placeholder } from './Components';
import { LabelStyled, RequiredSignStyled } from './Select.styled';
import { SelectOptionT } from 'declarations/types/select';

type SelectPropsT<
  Option extends SelectOptionT = SelectOptionT,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<Props<Option, IsMulti, Group>, 'options' | 'value' | 'defaultValue'> & {
  options: SelectOptionT[];
  value: SelectOptionT['value'];
  onChange: (option: OnChangeValue<SelectOptionT['value'], IsMulti>) => void;
  onValueChange?: (option: OnChangeValue<SelectOptionT['value'], IsMulti>) => void;
  defaultValue?: SelectOptionT['value'];
  label?: string | ReactNode;
  isRequired?: boolean;
};

const defaultComponents: SelectComponentsConfig<SelectOptionT, false, GroupBase<SelectOptionT>> = {
  Control,
  Placeholder,
  Option,
};

export const getOptionByValue = (
  options: SelectOptionT[],
  value: SelectOptionT['value']
): SelectOptionT | null => options.find(opt => opt.value === value) || null;

const Select: FC<SelectPropsT<SelectOptionT>> = ({
  onChange,
  options,
  value,
  defaultValue,
  label,
  name,
  isRequired,
  onValueChange,
  ...restProps
}) => {
  const selectValue = useMemo(() => getOptionByValue(options, value), [options, value]);
  const selectDefaultValue = useMemo(
    () => (defaultValue ? getOptionByValue(options, defaultValue) : undefined),
    [options, defaultValue]
  );

  const handleChangeValue = useCallback(
    (newValue: SingleValue<SelectOptionT>) => {
      if (newValue) {
        onChange(newValue.value);
        onValueChange?.(newValue.value);
      }
    },
    [onChange, onValueChange]
  );

  return (
    <>
      {label && (
        <LabelStyled htmlFor={name}>
          {label}
          {isRequired && <RequiredSignStyled>&nbsp;*</RequiredSignStyled>}
        </LabelStyled>
      )}
      <ReactSelect
        inputId={name}
        name={name}
        value={selectValue}
        defaultValue={selectDefaultValue}
        options={options}
        components={defaultComponents}
        onChange={handleChangeValue}
        {...restProps}
      />
    </>
  );
};

export default Select;
